import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Logo } from './Logo'
import { AccountControl } from './AccountControl'
import { useUI } from '@/lib/ui'
import { SocialSetSwitcher } from '@/features/channels/SocialSets'

type NavItem = {
  to: string
  label: string
  section: 'Operate' | 'Review' | 'Configure' | 'Later'
  icon: string
  badge?: string
}

const NAV: NavItem[] = [
  { to: '/calendar', label: 'Calendar', section: 'Operate', icon: 'calendar' },
  { to: '/inbox', label: 'Posts', section: 'Operate', icon: 'inbox' },
  { to: '/replies', label: 'Replies', section: 'Operate', icon: 'replies' },
  { to: '/analytics', label: 'Analytics', section: 'Review', icon: 'analytics' },
  { to: '/media', label: 'Media', section: 'Review', icon: 'media' },
  { to: '/plugs', label: 'Channels', section: 'Configure', icon: 'channels' },
  { to: '/settings', label: 'Settings', section: 'Configure', icon: 'settings' },
  { to: '/agent', label: 'Agent', section: 'Later', icon: 'agent', badge: 'Coming soon' },
]

const MOBILE_NAV = NAV.filter((item) => item.section !== 'Later').slice(0, 5)

function NavIcon({ kind, size = 14 }: { kind: string; size?: number }) {
  const paths: Record<string, React.ReactNode> = {
    agent: <><path d="M5 7.5A2.5 2.5 0 0 1 7.5 5h9A2.5 2.5 0 0 1 19 7.5v7a2.5 2.5 0 0 1-2.5 2.5h-6L6 20v-3.8A2.5 2.5 0 0 1 5 14.5z" /><path d="M9 10h6M9 13h4" /></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18M8 14h2M14 14h2M8 18h2" /></>,
    inbox: <><path d="M4 5h16l2 7v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6z" /><path d="M2 13h5l2 3h6l2-3h5" /></>,
    replies: <><path d="M21 15a4 4 0 0 1-4 4H8l-5 3v-7a4 4 0 0 1-1-2.6V7a4 4 0 0 1 4-4h11a4 4 0 0 1 4 4z" /><path d="M7 9h10M7 13h6" /></>,
    analytics: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,
    media: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>,
    channels: <><path d="M8 12h8M12 8v8" /><path d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4z" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1z" /></>,
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {paths[kind]}
    </svg>
  )
}

export function Sidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const openComposer = useUI((state) => state.openComposer)
  const [query, setQuery] = useState('')
  const [hovered, setHovered] = useState<string | null>(null)
  const [box, setBox] = useState<{ top: number; height: number } | null>(null)
  const navRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Record<string, HTMLAnchorElement | null>>({})

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return normalized ? NAV.filter((item) => item.label.toLowerCase().includes(normalized)) : NAV
  }, [query])

  useLayoutEffect(() => {
    const container = navRef.current
    const key = hovered ?? NAV.find((item) => location.pathname.startsWith(item.to))?.to
    const target = key ? itemRefs.current[key] : null
    if (!container || !target) return
    const containerRect = container.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()
    setBox({ top: targetRect.top - containerRect.top, height: targetRect.height })
  }, [hovered, location.pathname, filtered])

  return (
    <>
      <aside className="hidden w-[244px] shrink-0 flex-col border-r border-line bg-surface p-2 md:flex">
        <button type="button" onClick={() => navigate('/calendar')} className="mb-2 flex w-full items-center gap-2.5 rounded-control p-1.5 text-left transition hover:bg-hover active:scale-[0.98]">
          <Logo size={32} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium leading-tight text-ink-900">Hey Tasky</span>
            <span className="block truncate text-[11px] leading-tight text-ink-400">Publishing workspace</span>
          </span>
          <span className="size-1.5 rounded-full bg-green" title="Workspace online" />
        </button>

        <label className="mb-1 flex h-8 items-center gap-2 rounded-control bg-inset px-2.5 shadow-hairline">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Quick search" aria-label="Filter navigation" className="min-w-0 flex-1 bg-transparent text-[12.5px] text-ink-900 placeholder:text-ink-400" />
          <kbd className="grid size-[18px] place-items-center rounded-[5px] bg-surface font-mono text-[9px] text-ink-400 shadow-hairline">/</kbd>
        </label>

        <button type="button" onClick={() => openComposer()} className="mb-2 flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-[13px] font-medium text-accent transition hover:bg-accent-tint active:scale-[0.98]">
          <span className="min-w-0 flex-1 truncate text-left">New post</span>
          <span className="grid size-4 place-items-center rounded-full bg-accent text-white">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden><path d="M12 5v14M5 12h14" /></svg>
          </span>
        </button>

        <div ref={navRef} onMouseLeave={() => setHovered(null)} className="relative flex min-h-0 flex-1 flex-col overflow-y-auto">
          <span aria-hidden className="pointer-events-none absolute inset-x-0 rounded-[7px] bg-hover" style={{ top: box?.top ?? 0, height: box?.height ?? 0, opacity: box ? 1 : 0, transition: 'top 220ms cubic-bezier(0.23,1,0.32,1), opacity 150ms ease' }} />
          {(['Operate', 'Review', 'Configure', 'Later'] as const).map((section) => {
            const items = filtered.filter((item) => item.section === section)
            if (items.length === 0) return null
            return (
              <div key={section} className={section === 'Later' ? 'mt-auto mb-2' : 'mb-2'}>
                <p className="px-2 pb-1 pt-1 text-[10px] font-medium uppercase tracking-[0.08em] text-ink-400">{section === 'Later' ? 'Parked' : section}</p>
                <div className="flex flex-col gap-px">
                  {items.map((item) => {
                    return (
                      <NavLink key={item.to} ref={(element) => { itemRefs.current[item.to] = element }} to={item.to} onMouseEnter={() => setHovered(item.to)} onFocus={() => setHovered(item.to)} onBlur={() => setHovered(null)} className={({ isActive }) => `group relative z-10 flex items-center gap-2 rounded-[7px] px-2 py-1.5 text-[13px] transition active:scale-[0.98] ${isActive ? 'font-medium text-ink-900' : 'text-ink-600'}`}>
                        <span className="text-ink-400 group-aria-[current=page]:text-ink-900"><NavIcon kind={item.icon} /></span>
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {item.badge && <span className="rounded-full bg-inset px-1.5 py-0.5 text-[9px] font-medium text-ink-400 shadow-hairline">{item.badge}</span>}
                      </NavLink>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        <div className="border-t border-line pt-2">
          <p className="px-2 pb-1 text-[9.5px] font-medium uppercase tracking-[0.08em] text-ink-600">Posting as</p>
          <div className="mb-2">
            <SocialSetSwitcher />
          </div>
          <div className="border-t border-line pt-2">
            <p className="px-2 pb-1 text-[9.5px] font-medium uppercase tracking-[0.08em] text-ink-600">Hey Tasky account</p>
            <AccountControl />
          </div>
        </div>
      </aside>

      <header className="fixed inset-x-0 top-0 z-40 flex h-12 items-center border-b border-line bg-surface px-3 md:hidden">
        <button type="button" onClick={() => navigate('/calendar')} className="flex items-center gap-2">
          <Logo size={28} />
          <span className="text-[13px] font-medium text-ink-900">Hey Tasky</span>
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => openComposer()} className="rounded-control bg-ink-900 px-2.5 py-1.5 text-[11.5px] font-medium text-surface">New post</button>
          <AccountControl compact />
        </div>
      </header>

      <nav className="fixed inset-x-2 bottom-2 z-50 grid grid-cols-5 rounded-[14px] bg-surface p-1 shadow-overlay md:hidden" aria-label="Primary navigation">
        {MOBILE_NAV.map((item) => (
          <NavLink key={item.to} to={item.to} className={({ isActive }) => `flex flex-col items-center gap-0.5 rounded-control py-1.5 text-[9.5px] ${isActive ? 'bg-hover text-ink-900' : 'text-ink-400'}`}>
            <NavIcon kind={item.icon} size={15} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  )
}
